import { useEffect, useState, type FormEvent } from "react";
import type { PricingConfig } from "@photography-booking/shared";
import { getPricing, updatePricing } from "../api/admin-client.js";
import { AdminPanel, useAdminStatus } from "./AdminPanel.js";

interface PricingAdminProps {
  token: string;
}

/** An editable rate row: label plus the hourly rate as a dollar string. */
interface RateRow {
  label: string;
  /** Hourly rate in dollars, held as the raw input string. */
  hourlyRate: string;
}

/** Cents → dollar string for display. */
function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Dollar string → integer cents, or null when not a valid non-negative number. */
function dollarsToCents(value: string): number | null {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return Math.round(parsed * 100);
}

/**
 * Pricing management view (Req 5.4). Loads the current per-shoot-type hourly
 * rates, lets the owner edit labels and hourly rates (entered in dollars,
 * persisted as integer cents), add or remove rate rows, and saves via
 * `PUT /admin/pricing`.
 */
export function PricingAdmin({ token }: PricingAdminProps): JSX.Element {
  const [rows, setRows] = useState<RateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { status, reportError, reportSuccess, clearStatus } = useAdminStatus();

  useEffect(() => {
    let cancelled = false;
    void getPricing(token).then((result) => {
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (result.ok) {
        setRows(
          (result.data.rates ?? []).map((rate) => ({
            label: rate.label,
            hourlyRate: centsToDollars(rate.hourlyRateCents),
          })),
        );
      } else {
        reportError(result.message);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token, reportError]);

  const updateRow = (index: number, field: keyof RateRow, value: string): void => {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const addRow = (): void => {
    setRows((current) => [...current, { label: "", hourlyRate: "" }]);
  };

  const removeRow = (index: number): void => {
    setRows((current) => current.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    clearStatus();

    if (rows.length === 0) {
      reportError("Add at least one shoot-type rate.");
      return;
    }

    const rates: { label: string; hourlyRateCents: number }[] = [];
    for (const row of rows) {
      const label = row.label.trim();
      if (label.length === 0) {
        reportError("Every rate needs a label.");
        return;
      }
      const cents = dollarsToCents(row.hourlyRate);
      if (cents === null) {
        reportError(`Enter a valid hourly rate for "${label}".`);
        return;
      }
      rates.push({ label, hourlyRateCents: cents });
    }

    setSaving(true);
    const result = await updatePricing(token, {
      rates,
    } as Omit<PricingConfig, "updatedAt">);
    setSaving(false);
    if (result.ok) {
      reportSuccess("Pricing updated.");
    } else {
      reportError(result.message);
    }
  };

  return (
    <AdminPanel
      title="Pricing"
      description="Set the per-hour rate for each shoot type shown on the public site."
      status={status}
    >
      {loading ? (
        <p className="admin-empty">Loading pricing…</p>
      ) : (
        <form className="admin-form" onSubmit={handleSubmit}>
          {rows.map((row, index) => (
            <div className="admin-rate-row" key={index}>
              <div className="admin-field admin-rate-row__label">
                <label
                  className="admin-field__label"
                  htmlFor={`rate-label-${index}`}
                >
                  Shoot type
                </label>
                <input
                  id={`rate-label-${index}`}
                  className="admin-field__input"
                  type="text"
                  placeholder="e.g. Bikini"
                  value={row.label}
                  onChange={(event) => updateRow(index, "label", event.target.value)}
                />
              </div>
              <div className="admin-field admin-rate-row__rate">
                <label
                  className="admin-field__label"
                  htmlFor={`rate-amount-${index}`}
                >
                  $ / hour
                </label>
                <input
                  id={`rate-amount-${index}`}
                  className="admin-field__input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={row.hourlyRate}
                  onChange={(event) =>
                    updateRow(index, "hourlyRate", event.target.value)
                  }
                />
              </div>
              <button
                type="button"
                className="admin-button admin-button--danger admin-rate-row__remove"
                aria-label={`Remove ${row.label || "rate"}`}
                onClick={() => removeRow(index)}
              >
                Remove
              </button>
            </div>
          ))}

          <button type="button" className="admin-button" onClick={addRow}>
            + Add shoot type
          </button>

          <button
            type="submit"
            className="admin-button admin-button--primary"
            disabled={saving}
          >
            {saving ? "Saving…" : "Save pricing"}
          </button>
        </form>
      )}
    </AdminPanel>
  );
}
